import { useImperativeHandle } from 'react';
import * as v from 'valibot';
import { useForm, useStore } from '@tanstack/react-form';

import { Button, InputField, SelectField } from '@nvision/ui';

import { useCountries } from '../../../hooks';
import { type Country, type ProviderSearchFormValues, providerSearchFormSchema } from '../../../models';

import styles from './provider-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface ProviderSearchFormProps {
  formRef: React.RefObject<SearchFormRef>;
  handleSubmit: (values: ProviderSearchFormValues) => void;
  handleReset: () => void;
}

const getDefaultSearchFormValues = (): ProviderSearchFormValues =>
  v.getDefaults(providerSearchFormSchema) as unknown as ProviderSearchFormValues;

export const ProviderSearchForm: React.FC<ProviderSearchFormProps> = ({ formRef, handleSubmit, handleReset }) => {
  const form = useForm({
    defaultValues: getDefaultSearchFormValues(),
    onSubmit: async (props) => {
      handleSubmit(props.value);
    },
    validators: {
      onChange: providerSearchFormSchema,
      onSubmit: providerSearchFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => {
      form.reset(getDefaultSearchFormValues());
      handleReset();
    },
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const { data: countries, isLoading: countriesLoading } = useCountries();

  const errorMap = useStore(form.store, (store) => store.errorMap.onSubmit);

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <div className={styles.fields}>
        <form.Field name="additional">
          {(fields) => (
            <>
              <form.Field name="additional.companyName">
                {(field) => (
                  <InputField
                    value={field.state.value ?? ''}
                    clearable
                    onChange={(e) => field.handleChange(e.target.value)}
                    label="Provider Name"
                    placeholder="Enter"
                    size="sm"
                    errorMessage={fields.state.meta.errorMap.onChange?.map((e) => e.message).join('. ')}
                  />
                )}
              </form.Field>
              <form.Field name="additional.scac">
                {(field) => (
                  <InputField
                    value={field.state.value ?? ''}
                    clearable
                    onChange={(e) => field.handleChange(e.target.value)}
                    label="SCAC"
                    placeholder="Enter"
                    size="sm"
                    errorMessage={fields.state.meta.errorMap.onChange?.map((e) => e.message).join('. ')}
                  />
                )}
              </form.Field>
              <form.Field name="country">
                {(field) => (
                  <SelectField
                    isRequired
                    value={field.state.value as unknown as Country | null}
                    label="Country"
                    options={countries?.list || []}
                    placeholder="Select"
                    size="sm"
                    onBlur={field.handleBlur}
                    onSelectionChange={(country) =>
                      country && field.handleChange({ id: String(country.id), label: country.label })
                    }
                    errorMessage={errorMap?.['country.id']?.map((e) => e?.message).join('.')}
                    isLoading={countriesLoading}
                  />
                )}
              </form.Field>
              <form.Field name="additional.portPostal">
                {(field) => (
                  <InputField
                    value={field.state.value ?? ''}
                    clearable
                    onChange={(e) => field.handleChange(e.target.value)}
                    label="Port Postal"
                    placeholder="Enter"
                    size="sm"
                    errorMessage={fields.state.meta.errorMap.onChange?.map((e) => e.message).join('. ')}
                  />
                )}
              </form.Field>
            </>
          )}
        </form.Field>
      </div>
      <Button type="submit">Search</Button>
      <Button secondary outline onClick={() => formRef.current?.reset()}>
        Reset
      </Button>
    </form>
  );
};
